/**
 * "Overdrive" — original speed-gauntlet level (difficulty 6/10).
 * Escalates through the speed bands (1× → 2× → 3×), flips gravity for an
 * inverted ceiling run, and finishes with a synced dual corridor back at 2×.
 * Spacing scales with each band's jump span (≈4.5 / 5.6 / 6.7 blocks).
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0, hanging = false): void =>
  push({ type: 'spike', ...cell(bx, by), ...(hanging ? { flipY: true } : {}) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};

// --- Warm-up at 1× (0–38) ------------------------------------------------------
spike(18);
spike(28);
push({ type: 'collectible', ...cell(23, 2), props: { kind: 'shard' } });

// --- 2× band (40–78) ------------------------------------------------------------
portal(40, 'speed:two');
spike(52);
spike(62);
spike(63);
spike(74);

// --- 3× band (80–128) -----------------------------------------------------------
portal(80, 'speed:three');
spike(94);
spike(106);
spike(107);
spike(120);
push({ type: 'collectible', ...cell(113, 2), props: { kind: 'shard' } });

// --- Inverted ceiling run, back to 2× (130–168) -----------------------------------
portal(130, 'speed:two');
portal(132, 'gravity:up');
ceiling(134, 168, 6); // the inverted "floor"
spike(144, 5, true);
spike(145, 5, true); // hanging = upright for the inverted runner
spike(156, 5, true);
spike(157, 5, true);
portal(170, 'gravity:down');

// --- Dual corridor at 2× (174–208) --------------------------------------------------
portal(174, 'dual:on');
ceiling(176, 206, 7);
spike(186);
spike(187, 6, true);
spike(196);
spike(197, 6, true);
portal(208, 'dual:off');

// --- Wind-down and finale (212–240) ---------------------------------------------------
portal(212, 'speed:one');
spike(222);
spike(223);
spike(230);
push({ type: 'collectible', ...cell(226, 2), props: { kind: 'shard' } });
push({ type: 'endgate', ...cell(240, 2) });

export const OVERDRIVE: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Overdrive',
    author: 'Shape Shifters Team',
    description: 'Three speeds. One rhythm. No brakes.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 6,
    tags: ['speed', 'official'],
  },
  song: { id: 'voltage', bpm: 150, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#fbbf24',
    hazardColor: '#f43f5e',
    backgroundColor: '#1c0a05',
  },
  objects,
};
